import { supabase } from '../lib/supabase';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { format } from 'date-fns';

export const invoiceService = {
  /**
   * Generate invoice PDF from billing record
   */
  async generateInvoicePDF(billingRecord, memberProfile, tenantInfo, subscriptionDetails) {
    try {
      // Create invoice HTML template
      const invoiceHTML = this.createInvoiceHTML(billingRecord, memberProfile, tenantInfo, subscriptionDetails);
      
      // Create temporary container
      const container = document.createElement('div');
      container.innerHTML = invoiceHTML;
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.width = '800px';
      document.body?.appendChild(container);

      // Convert HTML to canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      // Remove temporary container
      document.body?.removeChild(container);

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas?.height * imgWidth) / canvas?.width;
      const imgData = canvas?.toDataURL('image/png');

      pdf?.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

      return {
        success: true,
        pdf,
        fileName: `Invoice_${billingRecord?.invoice_number}.pdf`
      };
    } catch (error) {
      console.error('Error generating invoice PDF:', error);
      return {
        success: false,
        error: error?.message || 'Failed to generate invoice PDF'
      };
    }
  },

  /**
   * Create HTML template for invoice
   */
  createInvoiceHTML(billingRecord, memberProfile, tenantInfo, subscriptionDetails) {
    const invoiceDate = format(new Date(billingRecord?.billing_date), 'MMM dd, yyyy');
    const dueDate = format(new Date(billingRecord?.due_date), 'MMM dd, yyyy');
    
    return `
      <div style="font-family: Arial, sans-serif; padding: 40px; background: white; color: #333;">
        <!-- Header -->
        <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 40px; border-bottom: 3px solid #667eea; padding-bottom: 20px;">
          <div>
            <h1 style="margin: 0; color: #667eea; font-size: 32px;">${tenantInfo?.business_name || 'Fitness Center'}</h1>
            <p style="margin: 5px 0; color: #6b7280;">${tenantInfo?.address || ''}</p>
            <p style="margin: 5px 0; color: #6b7280;">${tenantInfo?.phone || ''}</p>
            <p style="margin: 5px 0; color: #6b7280;">${tenantInfo?.email || ''}</p>
          </div>
          <div style="text-align: right;">
            <h2 style="margin: 0; color: #111827; font-size: 28px;">INVOICE</h2>
            <p style="margin: 10px 0; font-size: 16px; color: #6b7280;">#${billingRecord?.invoice_number}</p>
            <p style="margin: 5px 0; color: #6b7280;">Date: ${invoiceDate}</p>
            <p style="margin: 5px 0; color: #6b7280;">Due: ${dueDate}</p>
          </div>
        </div>

        <!-- Bill To -->
        <div style="margin-bottom: 40px;">
          <h3 style="margin: 0 0 15px 0; color: #111827; font-size: 18px;">Bill To:</h3>
          <p style="margin: 5px 0; font-size: 16px; font-weight: bold;">${memberProfile?.full_name || 'Member'}</p>
          <p style="margin: 5px 0; color: #6b7280;">${memberProfile?.email || ''}</p>
        </div>

        <!-- Invoice Details Table -->
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
          <thead>
            <tr style="background: #f3f4f6;">
              <th style="padding: 15px; text-align: left; border-bottom: 2px solid #e5e7eb; color: #374151; font-weight: 600;">Description</th>
              <th style="padding: 15px; text-align: center; border-bottom: 2px solid #e5e7eb; color: #374151; font-weight: 600;">Billing Cycle</th>
              <th style="padding: 15px; text-align: right; border-bottom: 2px solid #e5e7eb; color: #374151; font-weight: 600;">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="padding: 20px 15px; border-bottom: 1px solid #e5e7eb;">
                <div style="font-weight: 600; color: #111827; margin-bottom: 5px;">${subscriptionDetails?.plan_name || 'Membership Subscription'}</div>
                <div style="color: #6b7280; font-size: 14px;">${subscriptionDetails?.plan_code || ''}</div>
              </td>
              <td style="padding: 20px 15px; text-align: center; border-bottom: 1px solid #e5e7eb; color: #6b7280;">
                ${billingRecord?.billing_cycle || 'Monthly'}
              </td>
              <td style="padding: 20px 15px; text-align: right; border-bottom: 1px solid #e5e7eb; color: #111827; font-weight: 600;">
                $${parseFloat(billingRecord?.amount || 0)?.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Totals -->
        <div style="margin-left: auto; width: 300px;">
          <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e5e7eb;">
            <span style="color: #6b7280;">Subtotal:</span>
            <span style="color: #111827; font-weight: 600;">$${parseFloat(billingRecord?.amount || 0)?.toFixed(2)}</span>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e5e7eb;">
            <span style="color: #6b7280;">Tax:</span>
            <span style="color: #111827; font-weight: 600;">$${parseFloat(billingRecord?.tax_amount || 0)?.toFixed(2)}</span>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 15px 0; background: #f3f4f6; margin: 10px -15px 0 -15px; padding-left: 15px; padding-right: 15px;">
            <span style="color: #111827; font-size: 18px; font-weight: bold;">Total:</span>
            <span style="color: #667eea; font-size: 20px; font-weight: bold;">$${parseFloat(billingRecord?.total_amount || 0)?.toFixed(2)}</span>
          </div>
        </div>

        <!-- Payment Status -->
        <div style="margin-top: 40px; padding: 20px; background: ${billingRecord?.payment_status === 'completed' ? '#d1fae5' : billingRecord?.payment_status === 'pending' ? '#fef3c7' : '#fee2e2'}; border-radius: 8px;">
          <p style="margin: 0; color: ${billingRecord?.payment_status === 'completed' ? '#065f46' : billingRecord?.payment_status === 'pending' ? '#92400e' : '#991b1b'}; font-weight: 600;">
            Payment Status: ${billingRecord?.payment_status?.toUpperCase() || 'PENDING'}
          </p>
        </div>

        <!-- Footer -->
        <div style="margin-top: 50px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 14px;">
          <p style="margin: 5px 0;">Thank you for your business!</p>
          <p style="margin: 5px 0;">For questions about this invoice, please contact ${tenantInfo?.email || 'support@gym.com'}</p>
        </div>
      </div>
    `;
  },

  /**
   * Download invoice PDF
   */
  async downloadInvoice(billingRecordId, tenantId) {
    try {
      // Fetch billing record with related data
      const { data: billingRecord, error: billingError } = await supabase
        ?.from('billing_records')
        ?.select(`
          *,
          member_subscriptions:subscription_id(
            id,
            billing_cycle,
            membership_plans:plan_id(plan_name, plan_code)
          ),
          user_profiles:member_id(id, full_name, email)
        `)
        ?.eq('id', billingRecordId)
        ?.eq('tenant_id', tenantId)
        ?.single();

      if (billingError) throw billingError;

      // Fetch tenant info
      const { data: tenantInfo, error: tenantError } = await supabase
        ?.from('tenants')
        ?.select('*')
        ?.eq('id', tenantId)
        ?.single();

      if (tenantError) throw tenantError;

      const memberProfile = billingRecord?.user_profiles;
      const subscriptionDetails = {
        plan_name: billingRecord?.member_subscriptions?.membership_plans?.plan_name,
        plan_code: billingRecord?.member_subscriptions?.membership_plans?.plan_code
      };

      // Generate PDF
      const result = await this.generateInvoicePDF(
        billingRecord,
        memberProfile,
        tenantInfo,
        subscriptionDetails
      );

      if (result?.success) {
        // Download PDF
        result?.pdf?.save(result?.fileName);
        return { success: true, fileName: result?.fileName };
      } else {
        throw new Error(result?.error);
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      return {
        success: false,
        error: error?.message || 'Failed to download invoice'
      };
    }
  },

  /**
   * Send invoice via email
   */
  async sendInvoiceEmail(billingRecordId, tenantId) {
    try {
      // Fetch billing record with related data
      const { data: billingRecord, error: billingError } = await supabase
        ?.from('billing_records')
        ?.select(`
          *,
          member_subscriptions:subscription_id(
            id,
            billing_cycle,
            membership_plans:plan_id(plan_name, plan_code)
          ),
          user_profiles:member_id(id, full_name, email)
        `)
        ?.eq('id', billingRecordId)
        ?.eq('tenant_id', tenantId)
        ?.single();

      if (billingError) throw billingError;

      // Fetch tenant info
      const { data: tenantInfo, error: tenantError } = await supabase
        ?.from('tenants')
        ?.select('*')
        ?.eq('id', tenantId)
        ?.single();

      if (tenantError) throw tenantError;

      // Send email via Edge Function
      const { data, error } = await supabase?.functions?.invoke('send-invoice-email', {
        body: {
          memberEmail: billingRecord?.user_profiles?.email,
          memberName: billingRecord?.user_profiles?.full_name,
          invoiceNumber: billingRecord?.invoice_number,
          invoiceDate: billingRecord?.billing_date,
          dueDate: billingRecord?.due_date,
          totalAmount: billingRecord?.total_amount,
          paymentStatus: billingRecord?.payment_status,
          tenantName: tenantInfo?.business_name,
          billingRecordId: billingRecordId,
          tenantId: tenantId
        }
      });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error sending invoice email:', error);
      return {
        success: false,
        error: error?.message || 'Failed to send invoice email'
      };
    }
  },

  /**
   * Get billing records with filters
   */
  async getBillingRecords(tenantId, filters = {}) {
    try {
      let query = supabase
        ?.from('billing_records')
        ?.select(`
          *,
          member_subscriptions:subscription_id(
            id,
            billing_cycle,
            membership_plans:plan_id(plan_name, plan_code)
          ),
          user_profiles:member_id(id, full_name, email, avatar_url)
        `)
        ?.eq('tenant_id', tenantId)
        ?.order('billing_date', { ascending: false });

      if (filters?.paymentStatus) {
        query = query?.eq('payment_status', filters?.paymentStatus);
      }

      if (filters?.startDate) {
        query = query?.gte('billing_date', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('billing_date', filters?.endDate);
      }

      if (filters?.memberId) {
        query = query?.eq('member_id', filters?.memberId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching billing records:', error);
      return {
        success: false,
        error: error?.message || 'Failed to fetch billing records'
      };
    }
  },

  /**
   * Generate invoice for subscription
   */
  async generateInvoiceForSubscription(subscriptionId, tenantId) {
    try {
      // Fetch subscription details
      const { data: subscription, error: subError } = await supabase
        ?.from('member_subscriptions')
        ?.select(`
          *,
          membership_plans:plan_id(plan_name, plan_code, price)
        `)
        ?.eq('id', subscriptionId)
        ?.eq('tenant_id', tenantId)
        ?.single();

      if (subError) throw subError;

      // Generate invoice number
      const invoiceNumber = `INV-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase()}`;

      // Calculate dates
      const billingDate = new Date();
      const dueDate = new Date();
      dueDate?.setDate(dueDate?.getDate() + 15); // 15 days payment term

      // Calculate amounts
      const amount = parseFloat(subscription?.final_price || subscription?.monthly_price || 0);
      const taxRate = 0.08; // 8% tax rate
      const taxAmount = amount * taxRate;
      const totalAmount = amount + taxAmount;

      // Create billing record
      const { data: billingRecord, error: billingError } = await supabase
        ?.from('billing_records')
        ?.insert({
          tenant_id: tenantId,
          member_id: subscription?.member_id,
          subscription_id: subscriptionId,
          invoice_number: invoiceNumber,
          billing_date: billingDate?.toISOString()?.split('T')?.[0],
          due_date: dueDate?.toISOString()?.split('T')?.[0],
          amount: amount,
          tax_amount: taxAmount,
          total_amount: totalAmount,
          payment_status: 'pending',
          billing_cycle: subscription?.billing_cycle || 'monthly'
        })
        ?.select()
        ?.single();

      if (billingError) throw billingError;

      return { success: true, data: billingRecord };
    } catch (error) {
      console.error('Error generating invoice:', error);
      return {
        success: false,
        error: error?.message || 'Failed to generate invoice'
      };
    }
  }
};

export default invoiceService;